import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import Logger from 'core/utils/Logger'

import { interpolate } from './helpers/interpolate'
import { LanguageContext } from './LanguageContext'
import {
  IProviderProps,
  IProviderState,
  ITranslationFunction,
  TIsLanguageScopeLoading,
  TLanguage,
  TScope,
} from './models/typesDefinition'

export const LanguageProvider = ({
  children,
  translationsLoaders,
  defaultScope,
  fallbackLanguage,
}: IProviderProps) => {
  const user = useSelector(selectUser)
  const [dictionary, setDictionary] = useState<Partial<IProviderState>>({})
  const [language, setLanguage] = useState(fallbackLanguage)
  const [isLanguageScopeLoading, setIsLanguageScopeLoading] = useState<TIsLanguageScopeLoading>({})
  const isAnyScopeLoading = Object.values(isLanguageScopeLoading).some(Boolean)

  const setLanguageScopeLoading = useCallback(
    (languageScope: string, isLoading: boolean) =>
      setIsLanguageScopeLoading((prev) => ({
        ...prev,
        [languageScope]: isLoading,
      })),
    []
  )

  const removeScope = useCallback((scope: string) => {
    setDictionary((prevState) => {
      const newState = { ...prevState }
      delete newState[scope]
      return newState
    })
  }, [])

  const loadTranslations = useCallback(
    async (lang: TLanguage, scope: TScope) => {
      const results = await Promise.all(translationsLoaders.map((load) => load(lang, scope)))
      return results.reduce((previousValue, currentValue) => {
        const merged = { ...previousValue }
        for (const key of Object.keys(currentValue)) {
          merged[key] = {
            ...merged[key],
            ...currentValue[key],
          }
        }
        return merged
      }, {})
    },
    [translationsLoaders]
  )

  const loadScope = useMemo(() => {
    const loadingScopes = new Set<string>()

    return async (language: string, scope: string) => {
      const loadingLanguageScope = `${language}-${scope}`
      if (!loadingScopes.has(loadingLanguageScope)) {
        setLanguageScopeLoading(loadingLanguageScope, true)

        loadingScopes.add(loadingLanguageScope)
        let result = await loadTranslations(language, scope)
        if (!Object.keys(result).length) {
          result[language] = {}
        }

        const hasLanguageTranslations = Boolean(
          result[language] && Object.keys(result[language]).length
        )
        if (
          fallbackLanguage !== language &&
          !hasLanguageTranslations &&
          !loadingScopes.has(`${fallbackLanguage}-${scope}`)
        ) {
          loadingScopes.add(`${fallbackLanguage}-${scope}`)

          setLanguage(fallbackLanguage)
          result = await loadTranslations(fallbackLanguage, scope)

          loadingScopes.delete(`${fallbackLanguage}-${scope}`)
        }

        setDictionary((prevState) => {
          return {
            ...prevState,
            [scope]: {
              ...prevState[scope],
              ...result,
            },
          }
        })
        loadingScopes.delete(loadingLanguageScope)

        setLanguageScopeLoading(loadingLanguageScope, false)
      }
    }
  }, [fallbackLanguage, loadTranslations, setLanguageScopeLoading])

  const initScope = useCallback(
    (scope: string) => loadScope(language, scope),
    [language, loadScope]
  )

  const translator = useCallback(
    (key: string, scope: string) => {
      if (!dictionary[scope]?.[language]) {
        loadScope(language, scope).catch(() => Logger.error('Translations are not loaded!'))
      }

      let value = dictionary[scope]?.[language]?.[key]
      if (!value) {
        value = `${scope}:${key}`
      }

      return value
    },
    [dictionary, language, loadScope]
  )

  const t: ITranslationFunction = useCallback(
    (fullKey, options) => {
      let scope: string
      let key: string

      if (fullKey.includes(':')) {
        ;[scope, key] = fullKey.split(':')
      } else {
        scope = defaultScope
        key = fullKey
      }

      let value = translator(key, scope)

      if (options) {
        value = interpolate(value, options)
      }

      return value
    },
    [defaultScope, translator]
  )

  const contextValue = useMemo(
    () => ({
      t,
      language,
      setLanguage,
      translationIsLoading: isAnyScopeLoading,
      isLanguageScopeLoading,
      removeScope,
      initScope,
    }),
    [initScope, isAnyScopeLoading, isLanguageScopeLoading, language, removeScope, t]
  )

  useEffect(() => {
    if (user.locale) {
      const language = user.locale.match(/[^-]+/)?.[0] || fallbackLanguage
      setLanguage(language)
    }
  }, [fallbackLanguage, user.locale])

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
}
